import { Label } from '@/components/base/label'
import { Icon } from '@/components/common/icons/Icon'
import { SliderWithTicks } from '@/components/SliderWithTicks'
import { DEBOUNCE_SLIDER_TIMEOUT_MS } from '@/helpers/constants'
import { validateMinMax } from '@/helpers/utils'
import { useOptimisticUpdateWithPromise } from '@/hooks/useOptimisticUpdateWithPromise'
import { useRoomStoreActions } from '@/stores/room'
import { useCallback, useId } from 'react'

// Range of microphone volume set from the SDK/server
const SPEAKER_VOLUME_RANGE = {
  MAX: 50,
  MIN: -50,
} as const

interface SliderSetSpeakerVolume {
  disabled: boolean
  memberId: string
  speakerVolume: number
}

export const SliderSetSpeakerVolume = ({
  disabled,
  memberId,
  speakerVolume,
}: SliderSetSpeakerVolume) => {
  const speakerVolumeId = useId()

  const { setSpeakerVolume } = useRoomStoreActions()

  const handleUpdateCallback = useCallback(
    (value: number) => {
      if (
        validateMinMax(
          value,
          SPEAKER_VOLUME_RANGE.MIN,
          SPEAKER_VOLUME_RANGE.MAX,
        )
      ) {
        return setSpeakerVolume({ memberId, value })
      } else {
        throw new Error('Invalid speaker volume value')
      }
    },
    [memberId, setSpeakerVolume],
  )

  const { optimisticValue, handleUpdate } = useOptimisticUpdateWithPromise(
    speakerVolume,
    handleUpdateCallback,
    DEBOUNCE_SLIDER_TIMEOUT_MS,
  )

  const handleValueChange = (value: number[]) => {
    const valueToUpdate = value.at(0)
    if (valueToUpdate !== undefined) {
      handleUpdate(valueToUpdate)
    }
  }

  return (
    <>
      <div className="flex items-center gap-x-3 py-4 pr-2">
        <Icon size="xl" tag="headphones" />
        <Label htmlFor={speakerVolumeId}>Speaker Volume</Label>
      </div>
      <SliderWithTicks
        disabled={disabled}
        id={speakerVolumeId}
        max={SPEAKER_VOLUME_RANGE.MAX}
        min={SPEAKER_VOLUME_RANGE.MIN}
        onValueChange={handleValueChange}
        step={1}
        stops={9}
        value={[optimisticValue]}
      />
    </>
  )
}
